import {
  useEffect,
  useMemo,
  useState,
} from "react";

import { useAuth } from "../../auth/AuthContext";

import {
  logAuditEvent,
} from "../../core/audit.service";

import {
  loadClients,
} from "../clientes/clientes.service";

import type {
  Client,
} from "../clientes/types";

import ComandaDetalhes from "./components/ComandaDetalhes";

import {
  createComanda,
  loadClosedCashDates,
  loadClosedComandas,
  loadOpenComandas,
} from "./comandas.service";

import type {
  Comanda,
  ComandaTipo,
  SaidaInternaMotivo,
} from "./types";

import "./Comandas.css";

function toLocalDateInput(
  date: Date,
) {
  const year = date.getFullYear();
  const month = String(
    date.getMonth() + 1,
  ).padStart(2, "0");
  const day = String(
    date.getDate(),
  ).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function formatDateTime(
  value: string | null,
) {
  if (!value) return "—";

  return new Intl.DateTimeFormat(
    "pt-BR",
    {
      dateStyle: "short",
      timeStyle: "short",
    },
  ).format(new Date(value));
}

export default function Comandas() {
  const {
    profile,
    session,
    can,
  } = useAuth();

  const [
    comandas,
    setComandas,
  ] =
    useState<Comanda[]>([]);

  const [
    closedComandas,
    setClosedComandas,
  ] = useState<Comanda[]>([]);

  const [closedCashDates, setClosedCashDates] =
    useState<string[]>([]);

  const [
    activeTab,
    setActiveTab,
  ] = useState<"abertas" | "pendentes" | "fechadas">(
    "abertas",
  );

  const today = useMemo(
    () => toLocalDateInput(new Date()),
    [],
  );

  const monthStart = useMemo(() => {
    const date = new Date();
    date.setDate(1);
    return toLocalDateInput(date);
  }, []);

  const [
    closedStart,
    setClosedStart,
  ] = useState(monthStart);

  const [
    closedEnd,
    setClosedEnd,
  ] = useState(today);

  const [
    selectedComanda,
    setSelectedComanda,
  ] =
    useState<Comanda | null>(
      null,
    );

  const [
    clients,
    setClients,
  ] =
    useState<Client[]>([]);

  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const [
    saving,
    setSaving,
  ] =
    useState(false);

  const [
    search,
    setSearch,
  ] =
    useState("");

  const [
    showNew,
    setShowNew,
  ] =
    useState(false);

  const [
    newType,
    setNewType,
  ] = useState<ComandaTipo>(
    "venda",
  );

  const [
    motivoSaida,
    setMotivoSaida,
  ] = useState<SaidaInternaMotivo>(
    "consumo_interno",
  );

  const [
    observacaoSaida,
    setObservacaoSaida,
  ] = useState("");

  const [
    clienteNome,
    setClienteNome,
  ] =
    useState("");

  const [
    selectedClient,
    setSelectedClient,
  ] =
    useState<Client | null>(
      null,
    );

  const [
    error,
    setError,
  ] =
    useState("");

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);

        const [
          comandasResult,
          closedResult,
          clientsResult,
          closedCashDatesResult,
        ] =
          await Promise.all([
            loadOpenComandas(),
            loadClosedComandas(
              closedStart,
              closedEnd,
            ),
            loadClients(),
            loadClosedCashDates(),
          ]);

        setComandas(
          comandasResult,
        );

        setClosedComandas(
          closedResult,
        );

        setClients(
          clientsResult,
        );

        setClosedCashDates(
          closedCashDatesResult,
        );

        setError("");
      } catch (err) {
        console.error(
          err,
        );

        setError(
          "Não foi possível carregar as comandas.",
        );
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [closedStart, closedEnd]);

  const closedCashDatesSet = useMemo(
    () => new Set(closedCashDates),
    [closedCashDates],
  );

  const todayComandas = useMemo(
    () => comandas.filter((comanda) => {
      const openedDate = comanda.data_operacional ||
        toLocalDateInput(new Date(comanda.created_at));

      return openedDate === today &&
        !closedCashDatesSet.has(openedDate);
    }),
    [comandas, closedCashDatesSet, today],
  );

  const pendingComandas = useMemo(
    () => comandas.filter((comanda) => {
      const openedDate = comanda.data_operacional ||
        toLocalDateInput(new Date(comanda.created_at));

      return openedDate !== today ||
        closedCashDatesSet.has(openedDate);
    }),
    [comandas, closedCashDatesSet, today],
  );

  const filteredComandas =
    useMemo(() => {
      const term =
        search
          .trim()
          .toLowerCase();

      const source = activeTab === "fechadas"
        ? closedComandas
        : activeTab === "pendentes"
          ? pendingComandas
          : todayComandas;

      if (!term) {
        return source;
      }

      return source.filter(
        (comanda) =>
          comanda.cliente_nome
            .toLowerCase()
            .includes(term),
      );
    }, [
      comandas,
      closedComandas,
      pendingComandas,
      todayComandas,
      activeTab,
      search,
    ]);

  const clientSuggestions =
    useMemo(() => {
      const term =
        clienteNome
          .trim()
          .toLowerCase();

      if (!term) {
        return [];
      }

      return clients
        .filter(
          (client) =>
            client.nome
              .toLowerCase()
              .includes(term) ||
            (
              client.telefone ||
              ""
            )
              .toLowerCase()
              .includes(term),
        )
        .slice(0, 5);
    }, [
      clients,
      clienteNome,
    ]);

  const openSalesSummary = useMemo(() => {
    const source = activeTab === "pendentes"
      ? pendingComandas
      : todayComandas;

    const sales = source.filter(
      (comanda) => comanda.tipo === "venda",
    );

    return {
      count: sales.length,
      total: sales.reduce(
        (sum, comanda) => sum + Number(comanda.total || 0),
        0,
      ),
    };
  }, [activeTab, pendingComandas, todayComandas]);

  async function handleCreate() {
    const cleanName =
      clienteNome.trim();

    if (!cleanName) {
      setError(
        newType === "saida_interna"
          ? "Informe quem solicitou ou recebeu a retirada."
          : "Informe o nome do cliente.",
      );

      return;
    }

    try {
      setSaving(true);

      setError("");

      const created =
        await createComanda({
          cliente_id:
            selectedClient?.id ??
            null,

          cliente_nome:
            cleanName,

          created_by:
            profile?.id ??
            null,

          tipo: newType,

          motivo_saida:
            newType === "saida_interna"
              ? motivoSaida
              : null,

          observacao_saida:
            newType === "saida_interna"
              ? observacaoSaida
              : null,
        });

      setComandas(
        (current) => [
          created,
          ...current,
        ],
      );

      try {
        await logAuditEvent({
          userId:
            session?.user.id,

          employeeId:
            profile?.id,

          employeeName:
            profile?.nome,

          employeeLoginId:
            profile?.login_id,

          eventType:
            newType === "saida_interna"
              ? "SAIDA_INTERNA_ABERTA"
              : "COMANDA_ABERTA",

          level:
            "success",

          module:
            "comandas",

          entityType:
            "comanda",

          entityId:
            created.id,

          description:
            newType === "saida_interna"
              ? `${profile?.nome || "Funcionário"} abriu uma saída interna para ${created.cliente_nome}.`
              : `${profile?.nome || "Funcionário"} abriu uma comanda para ${created.cliente_nome}.`,

          metadata: {
            cliente:
              created.cliente_nome,

            cliente_id:
              created.cliente_id,

            tipo:
              created.tipo,

            motivo_saida:
              created.motivo_saida,
          },
        });
      } catch (
        auditError
      ) {
        console.error(
          "Falha na auditoria:",
          auditError,
        );
      }

      setClienteNome("");

      setSelectedClient(
        null,
      );

      setObservacaoSaida("");

      setShowNew(false);

      setSelectedComanda(
        created,
      );

      setActiveTab(
        closedCashDatesSet.has(today)
          ? "pendentes"
          : "abertas",
      );
    } catch (err) {
      console.error(
        err,
      );

      setError(
        "Não foi possível abrir a comanda.",
      );
    } finally {
      setSaving(false);
    }
  }

  function handleComandaChange(
    updated: Comanda,
  ) {
    if (
      updated.status ===
      "aberta"
    ) {
      setComandas(
        (current) =>
          current.map(
            (comanda) =>
              comanda.id ===
              updated.id
                ? updated
                : comanda,
          ),
      );

      setSelectedComanda(
        updated,
      );

      setClosedComandas(
        (current) =>
          current.filter(
            (comanda) =>
              comanda.id !== updated.id,
          ),
      );

      return;
    }

    setComandas(
      (current) =>
        current.filter(
          (comanda) =>
            comanda.id !==
            updated.id,
        ),
    );

    if (
      updated.status === "fechada" &&
      updated.tipo === "venda"
    ) {
      setClosedComandas(
        (current) => [
          updated,
          ...current.filter(
            (item) => item.id !== updated.id,
          ),
        ],
      );
    }
  }

  function handleComandaClosed(
    comandaId: string,
  ) {
    setComandas(
      (current) =>
        current.filter(
          (comanda) =>
            comanda.id !==
            comandaId,
        ),
    );

    setSelectedComanda(
      (current) =>
        current?.id ===
        comandaId
          ? null
          : current,
    );
  }

  function handleComandaReopened(
    updated: Comanda,
  ) {
    setClosedComandas(
      (current) =>
        current.filter(
          (comanda) =>
            comanda.id !== updated.id,
        ),
    );

    setComandas(
      (current) => [
        updated,
        ...current.filter(
          (comanda) =>
            comanda.id !== updated.id,
        ),
      ],
    );

    setSelectedComanda(updated);
    const openedDate = updated.data_operacional ||
      toLocalDateInput(new Date(updated.created_at));
    setActiveTab(
      openedDate === today &&
      !closedCashDatesSet.has(openedDate)
        ? "abertas"
        : "pendentes",
    );
  }

  if (
    !can(
      "comandas.view",
    )
  ) {
    return (
      <section className="comandas-page">
        <div className="comandas-empty">
          Você não possui acesso às comandas.
        </div>
      </section>
    );
  }

  return (
    <section className="comandas-page">
      <div className="comandas-header">
        <div>
          <span className="comandas-eyebrow">
            OPERAÇÃO
          </span>

          <h2>
            Comandas
          </h2>

          <p>
            Acompanhe as comandas abertas e o histórico de vendas.
          </p>
        </div>

        {can(
          "comandas.create",
        ) && (
          <div className="comandas-header-actions">
            <button
              type="button"
              className="internal-output-button"
              onClick={() => {
                setNewType("saida_interna");
                setShowNew(true);
                setClienteNome("");
                setSelectedClient(null);
                setMotivoSaida("consumo_interno");
                setObservacaoSaida("");
                setError("");
              }}
            >
              + Saída interna
            </button>

            <button
              type="button"
              className="new-comanda-button"
              onClick={() => {
                setNewType("venda");
                setShowNew(true);
                setClienteNome("");
                setSelectedClient(null);
                setObservacaoSaida("");
                setError("");
              }}
            >
              + Nova comanda
            </button>
          </div>
        )}
      </div>

      <div className="comandas-tabs">
        <button
          type="button"
          className={
            activeTab === "abertas"
              ? "active"
              : ""
          }
          onClick={() =>
            setActiveTab("abertas")
          }
        >
          Abertas hoje ({todayComandas.length})
        </button>

        <button
          type="button"
          className={
            activeTab === "pendentes"
              ? "active"
              : ""
          }
          onClick={() =>
            setActiveTab("pendentes")
          }
        >
          Pendentes anteriores ({pendingComandas.length})
        </button>

        <button
          type="button"
          className={
            activeTab === "fechadas"
              ? "active"
              : ""
          }
          onClick={() =>
            setActiveTab("fechadas")
          }
        >
          Fechadas
        </button>
      </div>

      {activeTab === "fechadas" && (
        <div className="comandas-history-filters">
          <label>
            De
            <input
              type="date"
              value={closedStart}
              max={closedEnd}
              onChange={(event) =>
                setClosedStart(event.target.value)
              }
            />
          </label>

          <label>
            Até
            <input
              type="date"
              value={closedEnd}
              min={closedStart}
              max={today}
              onChange={(event) =>
                setClosedEnd(event.target.value)
              }
            />
          </label>
        </div>
      )}

      {(activeTab === "abertas" || activeTab === "pendentes") && (
        <div className="comandas-open-summary">
          <div>
            <span>
              {activeTab === "pendentes"
                ? "COMANDAS PENDENTES ANTERIORES"
                : "COMANDAS DE VENDA ABERTAS HOJE"}
            </span>
            <strong>{openSalesSummary.count}</strong>
          </div>
          <div>
            <span>VALOR TOTAL EM ABERTO</span>
            <strong>
              {new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
              }).format(openSalesSummary.total)}
            </strong>
          </div>
          <small>
            {activeTab === "pendentes"
              ? "Ao receber, a entrada será registrada no caixa do dia do pagamento."
              : "Valores pendentes; ainda não são receita no Financeiro."}
          </small>
        </div>
      )}

      <div className="comandas-toolbar">
        <input
          type="search"
          placeholder="Buscar comanda..."
          value={search}
          onChange={(
            event,
          ) =>
            setSearch(
              event.target.value,
            )
          }
        />

        <span>
          {
            filteredComandas.length
          }{" "}
          {filteredComandas.length ===
          1
            ? activeTab === "fechadas"
              ? "fechada"
              : "aberta"
            : activeTab === "fechadas"
              ? "fechadas"
              : "abertas"}
        </span>
      </div>

      {error &&
        !showNew && (
          <div className="comandas-error">
            {error}
          </div>
        )}

      {loading ? (
        <div className="comandas-empty">
          Carregando comandas...
        </div>
      ) : filteredComandas.length ===
        0 ? (
        <div className="comandas-empty">
          {activeTab === "abertas"
            ? "Nenhuma comanda aberta hoje."
            : activeTab === "pendentes"
              ? "Nenhuma comanda pendente de período anterior."
              : "Nenhuma comanda fechada neste período."}
        </div>
      ) : (
        <div className="comandas-grid">
          {filteredComandas.map(
            (comanda) => (
              <button
                key={
                  comanda.id
                }
                type="button"
                className={`comanda-card ${
                  comanda.tipo === "saida_interna"
                    ? "internal"
                    : ""
                }`}
                onClick={() =>
                  setSelectedComanda(
                    comanda,
                  )
                }
              >
                <div className="comanda-avatar">
                  {comanda.cliente_nome
                    .charAt(0)
                    .toUpperCase()}
                </div>

                <div className="comanda-info">
                  <strong>
                    {
                      comanda.cliente_nome
                    }
                  </strong>

                  <span>
                    {comanda.status === "fechada"
                      ? `Fechada em ${formatDateTime(comanda.closed_at)}`
                      : activeTab === "pendentes"
                      ? `Aberta em ${formatDateTime(comanda.created_at)}`
                      : comanda.tipo === "saida_interna"
                      ? "Saída interna em aberto"
                      : "Comanda aberta"}
                  </span>
                </div>

                <strong className="comanda-total">
                  {new Intl.NumberFormat(
                    "pt-BR",
                    {
                      style:
                        "currency",

                      currency:
                        "BRL",
                    },
                  ).format(
                    Number(
                      comanda.total ||
                      0,
                    ),
                  )}
                </strong>
              </button>
            ),
          )}
        </div>
      )}

      {showNew && (
        <div
          className="comanda-modal-backdrop"
          onClick={() =>
            !saving &&
            setShowNew(
              false,
            )
          }
        >
          <div
            className="comanda-modal"
            onClick={(
              event,
            ) =>
              event.stopPropagation()
            }
          >
            <div className="comanda-modal-header">
              <div>
                <span className="comandas-eyebrow">
                  {newType === "saida_interna"
                    ? "SAÍDA INTERNA"
                    : "NOVA COMANDA"}
                </span>

                <h3>
                  {newType === "saida_interna"
                    ? "Registrar retirada"
                    : "Abrir comanda"}
                </h3>
              </div>

              <button
                type="button"
                onClick={() =>
                  setShowNew(
                    false,
                  )
                }
                disabled={
                  saving
                }
              >
                ×
              </button>
            </div>

            <label className="comanda-field">
              {newType === "saida_interna"
                ? "Quem solicitou ou recebeu"
                : "Cliente"}

              <input
                autoFocus
                value={
                  clienteNome
                }
                onChange={(
                  event,
                ) => {
                  setClienteNome(
                    event.target
                      .value,
                  );

                  setSelectedClient(
                    null,
                  );
                }}
                placeholder={
                  newType === "saida_interna"
                    ? "Nome do proprietário ou funcionário"
                    : "Nome ou telefone"
                }
              />

              {newType === "venda" && clientSuggestions.length >
                0 &&
                !selectedClient && (
                  <div className="comanda-client-suggestions">
                    {clientSuggestions.map(
                      (client) => (
                        <button
                          key={
                            client.id
                          }
                          type="button"
                          onClick={() => {
                            setSelectedClient(
                              client,
                            );

                            setClienteNome(
                              client.nome,
                            );
                          }}
                        >
                          <strong>
                            {
                              client.nome
                            }
                          </strong>

                          <span>
                            {client.telefone ||
                              "Sem telefone"}
                          </span>
                        </button>
                      ),
                    )}
                  </div>
                )}

              {selectedClient && (
                <div className="comanda-selected-client">
                  ✓ Cliente cadastrado
                  {selectedClient.telefone
                    ? ` · ${selectedClient.telefone}`
                    : ""}
                </div>
              )}
            </label>

            {newType === "saida_interna" && (
              <>
                <label className="comanda-field">
                  Motivo

                  <select
                    value={motivoSaida}
                    onChange={(event) =>
                      setMotivoSaida(
                        event.target.value as SaidaInternaMotivo,
                      )
                    }
                  >
                    <option value="consumo_interno">Consumo interno</option>
                    <option value="cortesia">Cortesia</option>
                    <option value="perda_avaria">Perda ou avaria</option>
                    <option value="vencimento">Vencimento</option>
                    <option value="uso_evento">Uso em evento</option>
                    <option value="outro">Outro</option>
                  </select>
                </label>

                <label className="comanda-field">
                  Observação (opcional)

                  <textarea
                    value={observacaoSaida}
                    onChange={(event) =>
                      setObservacaoSaida(event.target.value)
                    }
                    placeholder="Detalhes sobre a retirada"
                    rows={3}
                  />
                </label>

                <div className="internal-output-notice">
                  Os itens serão retirados do estoque e não gerarão entrada no Financeiro.
                </div>
              </>
            )}

            {error && (
              <div className="comandas-error">
                {error}
              </div>
            )}

            <div className="comanda-modal-actions">
              <button
                type="button"
                className="comanda-cancel"
                onClick={() =>
                  setShowNew(
                    false,
                  )
                }
                disabled={
                  saving
                }
              >
                Cancelar
              </button>

              <button
                type="button"
                className="comanda-save"
                onClick={
                  handleCreate
                }
                disabled={
                  saving ||
                  !clienteNome.trim()
                }
              >
                {saving
                  ? "Abrindo..."
                  : newType === "saida_interna"
                    ? "Abrir saída interna"
                    : "Abrir comanda"}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedComanda && (
        <ComandaDetalhes
          comanda={
            selectedComanda
          }
          onClose={() =>
            setSelectedComanda(
              null,
            )
          }
          onComandaChange={
            handleComandaChange
          }
          onComandaClosed={
            handleComandaClosed
          }
          onComandaReopened={
            handleComandaReopened
          }
        />
      )}
    </section>
  );
}
